import {
  Grid,
  IconButton,
  Box,
  Button,
  Chip,
  Typography,
  Skeleton,
  useMediaQuery,
  TextField,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import ContactPageIcon from "@mui/icons-material/ContactPage";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import DownloadIcon from "@mui/icons-material/Download";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import FeaturedPlayListOutlinedIcon from '@mui/icons-material/FeaturedPlayListOutlined';

import { useEffect, useState, useCallback, useRef } from "react";

import {
  getNotificationCertificates,
  deleteNotificationCertificate,
  updateNotificationCertificate,
  downloadNotificationCertificateFile,
  changeNotificationCertificateStatus,
  downloadGeneratedNotificationCertificatesMergedPdf,
} from "../../services/NotificationCertificateService";
import { useAuth } from "../../context/AuthContext";

import DataGridCustom from "../General/DatagridCustom";
import AppDialog from "../General/Dialog/AppDialog";
import ConfirmDialog from "../General/Dialog/ConfirmDialog";
import EditDialog from "../General/Dialog/EditDialog";
import ActionHeaderCard from "../General/ActionHeaderCard";
import { useToast } from "../../context/ToastContext";

export default function ManagerNotificationCertificate() {
  const { showToast } = useToast();
  const [notificationCertificates, setNotificationCertificates] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const { hasPermission } = useAuth();

  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterValues: [],
  });

  const [openEdit, setOpenEdit] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [editData, setEditData] = useState({
    label_color: "",
    label_code: "",
    bottom_barcode_code: "",
  });

  const [openPreview, setOpenPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusForm, setStatusForm] = useState({
    status: "",
    status_date: "",
    notes: "",
    result_status: "",
    label_color: "",
    label_code: "",
    bottom_barcode_code: "",
    final_file: null,
  });

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [previewLoading, setPreviewLoading] = useState(false);

  const [openTrackDialog, setOpenTrackDialog] = useState(false);
  const [selectedTrackRow, setSelectedTrackRow] = useState(null);

  const [openDownloadPdfDialog, setOpenDownloadPdfDialog] = useState(false);
  const [downloadPdfLoading, setDownloadPdfLoading] = useState(false);
  const [downloadStage, setDownloadStage] = useState("");
  const [downloadExcelFile, setDownloadExcelFile] = useState(null);
  const downloadExcelRef = useRef(null);

  const RESULT_STATUS_LABELS = {
    success: "Logrado",
    no_success: "No logrado",
  };

  const isSystemGeneratedCertificate = (row) => {
    return Boolean(row?.ocr_context?.generated);
  };

  const formatDateTime = (date) => {
    if (!date) return "—";

    const parsed = new Date(date);
    if (isNaN(parsed.getTime())) return "—";

    return parsed.toLocaleString("es-AR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTrackSteps = (row) => {
    const tracks = Array.isArray(row?.tracks) ? row.tracks : [];

    const trackByStatus = {
      generated: tracks.find((t) => t.status === "generated"),
      sent: tracks.find((t) => t.status === "sent"),
      finished: tracks.find((t) => t.status === "finished"),
      cancelled: tracks.find((t) => t.status === "cancelled"),
    };

    const currentStatus = row?.status;

    return [
      {
        key: "generated",
        title: "Generada",
        state:
          trackByStatus.generated || row?.generated_at || row?.created_at
            ? "done"
            : "pending",
        date:
          trackByStatus.generated?.status_date ||
          row?.generated_at ||
          row?.created_at,
        notes: trackByStatus.generated?.notes || null,
      },
      {
        key: "sent",
        title: "Enviada",
        state: trackByStatus.sent
          ? "done"
          : currentStatus === "generated"
            ? "pending"
            : ["finished", "cancelled"].includes(currentStatus)
              ? "error"
              : "pending",
        date: trackByStatus.sent?.status_date || row?.sent_at || null,
        notes: trackByStatus.sent?.notes || null,
        subtitle:
          ["finished", "cancelled"].includes(currentStatus) &&
          !trackByStatus.sent
            ? "No se registró paso previo de envío."
            : null,
      },
      {
        key: "finished",
        title: "Finalizada",
        state:
          trackByStatus.finished || currentStatus === "finished"
            ? "done"
            : "pending",
        date: trackByStatus.finished?.status_date || row?.finished_at || null,
        notes: trackByStatus.finished?.notes || null,
        resultStatus: row?.result_status,
      },
      {
        key: "cancelled",
        title: "Baja",
        state:
          trackByStatus.cancelled || currentStatus === "cancelled"
            ? "done"
            : "pending",
        date: trackByStatus.cancelled?.status_date || row?.cancelled_at || null,
        notes: trackByStatus.cancelled?.notes || null,
      },
    ];
  };

  const TrackStepIcon = ({ state }) => {
    if (state === "done") {
      return <CheckCircleRoundedIcon sx={{ color: "success.main" }} />;
    }

    if (state === "error") {
      return <CancelRoundedIcon sx={{ color: "error.main" }} />;
    }

    return <RadioButtonUncheckedRoundedIcon sx={{ color: "text.disabled" }} />;
  };

  const handleOpenTrackDialog = (row) => {
    setSelectedTrackRow(row);
    setOpenTrackDialog(true);
  };

  const handleCloseTrackDialog = () => {
    setOpenTrackDialog(false);
    setSelectedTrackRow(null);
  };

  const fetchNotificationCertificates = useCallback(async () => {
    setLoading(true);
    try {
      const { page, pageSize } = paginationModel;
      const search = filterModel?.quickFilterValues?.[0] || "";

      const res = await getNotificationCertificates({
        page: page + 1,
        perPage: pageSize,
        search,
      });

      setNotificationCertificates(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error(error);
      showToast(
        error?.response?.data?.message ||
          error?.message ||
          "Error al cargar las cédulas de notificación.",
      "error")
    } finally {
      setLoading(false);
    }
  }, [paginationModel, filterModel, showToast]);

  useEffect(() => {
    fetchNotificationCertificates();
  }, [fetchNotificationCertificates]);

  const handleOpenDeleteConfirm = (id) => {
    setSelectedDeleteId(id);
    setOpenDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!selectedDeleteId) return;

    try {
      setDeleteLoading(true);

      const res = await deleteNotificationCertificate(selectedDeleteId);
      if (res.data?.status === "success") {
        showToast(res.data.message, "success");
        setOpenEdit(false);
        setOpenDeleteConfirm(false);
        setSelectedDeleteId(null);
        await fetchNotificationCertificates();
      }
    } catch (error) {
      console.error(error);
      showToast("Error al eliminar cédula.", "error")
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEditOpen = (row) => {
    setSelectedRow(row);
    setEditData({
      label_color: row.label_color || "",
      label_code: row.label_code || "",
      bottom_barcode_code: row.bottom_barcode_code || "",
    });
    setOpenEdit(true);
  };

  const handleEditSave = async () => {
    if (!selectedRow) return;

    try {
      const payload = {
        label_color: editData.label_color || null,
        label_code: editData.label_code || null,
      };

      if (!isSystemGeneratedCertificate(selectedRow)) {
        payload.bottom_barcode_code = editData.bottom_barcode_code || null;
      }

      const res = await updateNotificationCertificate(selectedRow.id, payload);

      if (res.data?.status === "success") {
        showToast(res.data.message, "success");
        setOpenEdit(false);
        await fetchNotificationCertificates();
      }
    } catch (error) {
      console.error(error);
      showToast("Error al actualizar cédula.", "error")
    }
  };

  const handleOpenStatusDialog = (row) => {
    setSelectedRow(row);
    setStatusForm({
      status: "",
      status_date: "",
      notes: "",
      result_status: "",
      label_color: row.label_color || "",
      label_code: row.label_code || "",
      bottom_barcode_code: row.bottom_barcode_code || "",
      final_file: null,
    });
    setOpenStatusDialog(true);
  };

  const handleSaveStatus = async () => {
    if (!selectedRow) return;

    try {
      setStatusLoading(true);

      const formData = new FormData();
      formData.append("status", statusForm.status);
      formData.append("status_date", statusForm.status_date);
      formData.append("notes", statusForm.notes || "");
      formData.append("label_color", statusForm.label_color || "");
      formData.append("label_code", statusForm.label_code || "");

      if (!isSystemGeneratedCertificate(selectedRow)) {
        formData.append(
          "bottom_barcode_code",
          statusForm.bottom_barcode_code || "",
        );
      }

      if (statusForm.result_status) {
        formData.append("result_status", statusForm.result_status);
      }

      if (statusForm.final_file) {
        formData.append("final_file", statusForm.final_file);
      }

      const res = await changeNotificationCertificateStatus(
        selectedRow.id,
        formData,
      );

      if (res.data?.status === "success") {
        showToast(res.data.message, "success");
        setOpenStatusDialog(false);
        await fetchNotificationCertificates();
      }
    } catch (error) {
      console.error(error);
      showToast(
        error.response?.data?.message || "Error al registrar estado.",
      "error")
    } finally {
      setStatusLoading(false);
    }
  };

  const previewNotificationCertificate = async (id) => {
    try {
      setPreviewLoading(true);
      const response = await downloadNotificationCertificateFile(id);
      const fileURL = URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" }),
      );
      setPreviewUrl(fileURL);
      setOpenPreview(true);
    } catch (error) {
      console.error(error);
      showToast("No se pudo mostrar el archivo.", "error")
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setOpenPreview(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const getAllowedStatusOptions = () => {
    if (!selectedRow) return [];

    if (selectedRow.status === "generated") {
      return [
        { value: "sent", label: "Enviada" },
        { value: "cancelled", label: "Baja" },
      ];
    }

    if (selectedRow.status === "sent") {
      return [
        { value: "finished", label: "Finalizada" },
        { value: "cancelled", label: "Baja" },
      ];
    }

    return [];
  };

  const handleOpenDownloadPdfDialog = () => {
    setDownloadStage("");
    setDownloadExcelFile(null);
    setOpenDownloadPdfDialog(true);

    if (downloadExcelRef.current) {
      downloadExcelRef.current.value = "";
    }
  };

  const handleCloseDownloadPdfDialog = () => {
    setOpenDownloadPdfDialog(false);
    setDownloadStage("");
    setDownloadExcelFile(null);

    if (downloadExcelRef.current) {
      downloadExcelRef.current.value = "";
    }
  };

  const handleDownloadExcelChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();

    if (!["xls", "xlsx"].includes(ext)) {
      showToast("El archivo debe ser .xls o .xlsx", "error")
      return;
    }

    setDownloadExcelFile(file);
  };

  const handleDownloadGeneratedPdf = async () => {
    if (!downloadStage) {
      showToast("Debe seleccionar una etapa.", "error")
      return;
    }

    if (!downloadExcelFile) {
      showToast("Debe seleccionar un archivo Excel.", "error")
      return;
    }

    try {
      setDownloadPdfLoading(true);
      const formData = new FormData();
      formData.append("file", downloadExcelFile);
      formData.append("stage", downloadStage);

      const response =
        await downloadGeneratedNotificationCertificatesMergedPdf(formData);

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      let filename = "cedulas.pdf";
      const contentDisposition = response.headers?.["content-disposition"];
      const match = contentDisposition?.match(/filename="?([^"]+)"?/);

      if (match?.[1]) {
        filename = match[1];
      }

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);

      showToast(`PDF descargado correctamente: ${filename}`, "success");
      handleCloseDownloadPdfDialog();
    } catch (error) {
      try {
        if (error?.response?.data instanceof Blob) {
          const text = await error.response.data.text();
          const json = JSON.parse(text);

          showToast(
            json?.message || "Error al generar el PDF de cédulas.",
          "error")
          return;
        }

        showToast(
          error?.response?.data?.message ||
            error?.message ||
            "Error al generar el PDF de cédulas.",
        "error")
      } catch {
        showToast(
          error?.response?.data?.message ||
            error?.message ||
            "Error al generar el PDF de cédulas.",
        "error")
      }
    } finally {
      setDownloadPdfLoading(false);
    }
  };

  const columns = [
    { field: "code", headerName: "Código", flex: 1 },
    {
      field: "stage_text",
      headerName: "Etapa",
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.value || params.row.stage || "-"}
          variant="outlined"
        />
      ),
    },
    {
      field: "status_text",
      headerName: "Estado",
      flex: 1,
      renderCell: (params) => {
        const chipProps = {
          variant: "outlined",
          label: params.value || params.row.status || "-",
        };

        if (params.row.status === "generated") chipProps.color = "info";
        else if (params.row.status === "sent") chipProps.color = "warning";
        else if (params.row.status === "finished") chipProps.color = "success";
        else if (params.row.status === "cancelled") chipProps.color = "error";

        return <Chip {...chipProps} />;
      },
    },
    {
      field: "result_status_text",
      headerName: "Resultado",
      flex: 1,
      renderCell: (params) => {
        const chipProps = {
          variant: "outlined",
          label: params.value || "SIN RESULTADO",
        };

        if (params.row.result_status === "success") chipProps.color = "success";
        else if (params.row.result_status === "no_success")
          chipProps.color = "error";

        return <Chip {...chipProps} />;
      },
    },
    {
      field: "label_color_text",
      headerName: "Color Etiqueta",
      flex: 1,
      renderCell: (params) => {
        const value = params.value || "-";
        const chipProps = {
          label: value,
          variant: "outlined",
        };

        if (params.row.label_color === "yellow") {
          chipProps.sx = {
            color: "#9a6700",
            borderColor: "#eab308",
            backgroundColor: "#fef9c3",
          };
        }

        if (params.row.label_color === "orange") {
          chipProps.sx = {
            color: "#c2410c",
            borderColor: "#fb923c",
            backgroundColor: "#ffedd5",
          };
        }

        return <Chip {...chipProps} />;
      },
    },
    {
      field: "extracted_legal_file_code",
      headerName: "Código Expediente",
      flex: 1,
      renderCell: (params) => {
        const legalFileId = params.row.legal_file_id;
        const legalFileCode = params.value || "—";

        if (!legalFileId) {
          return <Typography variant="body2">{legalFileCode}</Typography>;
        }

        return (
          <Tooltip title="Ver detalle del expediente" placement="top">
            <Button
              component="a"
              href={`/legal/file/${legalFileId}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="text"
              size="small"
              sx={{
                minWidth: "auto",
                p: 0,
                fontWeight: 900,
                textTransform: "none",
                justifyContent: "flex-start",
                color: "#7d7d7d",
              }}
            >
              {legalFileCode}
            </Button>
          </Tooltip>
        );
      },
    },
    {
      field: "bottom_barcode_code",
      headerName: "Código Barra Inferior",
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Acciones",
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <>
          <IconButton
            color="primary"
            onClick={() => previewNotificationCertificate(params.row.id)}
          >
            <VisibilityIcon />
          </IconButton>

          <IconButton
            color="info"
            onClick={() => handleOpenTrackDialog(params.row)}
          >
            <TimelineOutlinedIcon />
          </IconButton>

          <IconButton
            color="warning"
            onClick={() => handleEditOpen(params.row)}
            disabled={!hasPermission("actualizar cedulas notificacion")}
          >
            <EditIcon />
          </IconButton>

          <IconButton
            color="secondary"
            onClick={() => handleOpenStatusDialog(params.row)}
            disabled={
              !hasPermission("actualizar cedulas notificacion") ||
              ["finished", "cancelled"].includes(params.row.status)
            }
          >
            <SyncAltIcon />
          </IconButton>

          <IconButton
            color="error"
            onClick={() => handleOpenDeleteConfirm(params.row.id)}
            disabled={!hasPermission("eliminar cedulas notificacion")}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  const NotificationCertificateTrackDialog = ({ open, onClose, row }) => {
    const steps = getTrackSteps(row);

    const activeStep = (() => {
      if (!row) return 0;
      if (row.status === "generated") return 1;
      if (row.status === "sent") return 2;
      if (row.status === "finished" || row.status === "cancelled") return 3;
      return 0;
    })();

    const getStatusChipSx = (status) => {
      const map = {
        pending: {
          bgcolor: "rgba(0,0,0,0.06)",
          color: "text.primary",
        },
        generated: {
          bgcolor: "rgba(25,118,210,0.10)",
          color: "primary.main",
        },
        sent: {
          bgcolor: "rgba(251,140,0,0.10)",
          color: "#c77700",
        },
        finished: {
          bgcolor: "rgba(46,125,50,0.10)",
          color: "success.main",
        },
        cancelled: {
          bgcolor: "rgba(211,47,47,0.10)",
          color: "error.main",
        },
      };

      const current = map[status] || map.pending;

      return {
        height: 24,
        borderRadius: 999,
        fontWeight: 900,
        bgcolor: current.bgcolor,
        color: current.color,
        "& .MuiChip-label": {
          px: 1.2,
          fontSize: 12,
        },
      };
    };

    const getResultChipSx = (resultStatus) => {
      const map = {
        success: {
          bgcolor: "rgba(46,125,50,0.10)",
          color: "success.main",
        },
        no_success: {
          bgcolor: "rgba(211,47,47,0.10)",
          color: "error.main",
        },
        pending: {
          bgcolor: "rgba(0,0,0,0.06)",
          color: "text.primary",
        },
      };

      const current = map[resultStatus] || map.pending;

      return {
        height: 24,
        borderRadius: 999,
        fontWeight: 900,
        bgcolor: current.bgcolor,
        color: current.color,
        "& .MuiChip-label": {
          px: 1.2,
          fontSize: 12,
        },
      };
    };

    const SummaryCard = ({ label, value, chip }) => (
      <Box
        sx={{
          p: 1.4,
          borderRadius: 3,
          border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
          background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.58)",
          minWidth: 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            opacity: 0.72,
            fontWeight: 800,
            display: "block",
            mb: 0.7,
          }}
        >
          {label}
        </Typography>

        {chip ? (
          chip
        ) : (
          <Typography
            sx={{
              fontWeight: 900,
              lineHeight: 1.2,
              overflowWrap: "anywhere",
            }}
          >
            {value || "—"}
          </Typography>
        )}
      </Box>
    );

    return (
      <AppDialog
        open={open}
        onClose={onClose}
        title={`Seguimiento de cédula${row?.code ? ` - ${row.code}` : ""}`}
        subtitle="Historial de estados registrados"
        icon={<TimelineOutlinedIcon />}
        maxWidth="sm"
        fullScreenMobile
        stickyHeader
        contentSx={{
          p: { xs: 1.2, sm: 2 },
          background: "transparent",
        }}
        paperSx={{ borderRadius: 4, overflow: "hidden" }}
        actions={
          <Button
            onClick={onClose}
            variant="contained"
            sx={{ borderRadius: 2.2, fontWeight: 900, px: 3 }}
          >
            Cerrar
          </Button>
        }
      >
        {!row ? null : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
                gap: 1.2,
              }}
            >
              <SummaryCard
                label="Estado actual"
                chip={
                  <Chip
                    label={row.status_text || row.status || "—"}
                    sx={getStatusChipSx(row.status)}
                  />
                }
              />

              <SummaryCard
                label="Resultado"
                chip={
                  <Chip
                    label={row.result_status_text || "SIN RESULTADO"}
                    sx={getResultChipSx(row.result_status)}
                  />
                }
              />
            </Box>

            <Box
              sx={{
                p: { xs: 1.3, sm: 1.6 },
                borderRadius: 3,
                border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
                background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.55)",
              }}
            >
              <Typography sx={{ fontWeight: 900, mb: 1.5 }}>
                Línea de seguimiento
              </Typography>

              <Stepper activeStep={activeStep} orientation="vertical">
                {steps.map((step) => (
                  <Step key={step.key} expanded>
                    <StepLabel
                      icon={<TrackStepIcon state={step.state} />}
                      sx={{
                        "& .MuiStepLabel-label": {
                          fontWeight: 900,
                          ...(step.state === "error" && {
                            color: "error.main",
                          }),
                        },
                      }}
                    >
                      {step.title}
                    </StepLabel>

                    <StepContent>
                      <Box
                        sx={{
                          pb: 1.6,
                          display: "grid",
                          gap: 0.7,
                        }}
                      >
                        <Typography variant="body2" sx={{ opacity: 0.82 }}>
                          Fecha: {formatDateTime(step.date)}
                        </Typography>

                        {step.resultStatus && step.key === "finished" ? (
                          <Box>
                            <Chip
                              size="small"
                              label={
                                RESULT_STATUS_LABELS[step.resultStatus] ||
                                step.resultStatus
                              }
                              sx={getResultChipSx(step.resultStatus)}
                            />
                          </Box>
                        ) : null}

                        {step.notes ? (
                          <Box
                            sx={{
                              p: 1,
                              borderRadius: 2.2,
                              bgcolor: "rgba(0,0,0,0.035)",
                              border: "1px solid rgba(0,0,0,0.06)",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}
                            >
                              {step.notes}
                            </Typography>
                          </Box>
                        ) : null}

                        {step.subtitle ? (
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              color: "error.main",
                              fontWeight: 700,
                            }}
                          >
                            {step.subtitle}
                          </Typography>
                        ) : null}
                      </Box>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </Box>

            {Array.isArray(row.final_files) && row.final_files.length > 0 ? (
              <Box
                sx={{
                  p: { xs: 1.3, sm: 1.6 },
                  borderRadius: 3,
                  border: "1px solid rgba(0,0,0,0.08)",
                  background: "rgba(255,255,255,0.55)",
                }}
              >
                <Typography sx={{ fontWeight: 900, mb: 1.4 }}>
                  Archivos finales
                </Typography>

                <Box sx={{ display: "grid", gap: 1 }}>
                  {row.final_files.map((file) => (
                    <Box
                      key={file.id}
                      sx={{
                        p: 1.3,
                        borderRadius: 2.6,
                        border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
                        background: isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.72)",
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 900,
                          lineHeight: 1.2,
                          overflowWrap: "anywhere",
                        }}
                      >
                        {file.original_name ||
                          file.file_name ||
                          "Archivo final"}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ opacity: 0.75, mt: 0.6 }}
                      >
                        Subido: {formatDateTime(file.uploaded_at)}
                      </Typography>

                      {file.notes ? (
                        <Box
                          sx={{
                            mt: 1,
                            p: 1,
                            borderRadius: 2,
                            bgcolor: "rgba(0,0,0,0.035)",
                            border: "1px solid rgba(0,0,0,0.06)",
                          }}
                        >
                          <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                            {file.notes}
                          </Typography>
                        </Box>
                      ) : null}
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : null}
          </Box>
        )}
      </AppDialog>
    );
  };

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }} spacing={2}>
      <Grid size={{ xs: 12 }}>

        <ActionHeaderCard
          icon={<ContactPageIcon />}
          title="Cédulas de Notificación"
          description="Administrar cédulas y descargar PDF para impresión."
          action={
            hasPermission("descargar cedula notificacion") ? (
              <Button
                onClick={handleOpenDownloadPdfDialog}
                variant="contained"
                startIcon={<DownloadIcon />}
                sx={{
                  borderRadius: 2,
                  fontWeight: 800,
                  textTransform: "none",
                }}
              >
                Descargar PDF
              </Button>
            ) : null
          }
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Box sx={{ width: "100%" }}>
          <DataGridCustom
            rows={notificationCertificates}
            columns={columns}
            title="Cédulas de Notificación"
            description="Vista general de todas las cédulas de notificación cargadas en el sistema."
            icon={<FeaturedPlayListOutlinedIcon sx={{ fontSize: 32 }} />}
            paginationModel={paginationModel}
            onPaginationChange={setPaginationModel}
            total={total}
            loading={loading}
            filterModel={filterModel}
            onFilterChange={setFilterModel}
            filterMode="server"
            isServerPagination={true}
          />
        </Box>
      </Grid>

      <EditDialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        onSave={handleEditSave}
        title={`Detalle de Cédula ${selectedRow ? `#${selectedRow.id}` : ""}`}
        subtitle="Revise la información general y edite únicamente los campos habilitados."
        values={{
          ...selectedRow,
          ...editData,
          code: selectedRow?.code || "",
          stage_text: selectedRow?.stage_text || selectedRow?.stage || "",
          status_text: selectedRow?.status_text || "",
          result_status_text: selectedRow?.result_status_text || "",
          original_name:
            selectedRow?.original_name || selectedRow?.file_name || "",
          storage_path: selectedRow?.storage_path || "",
          extracted_legal_file_code:
            selectedRow?.extracted_legal_file_code || "",
          created_at: selectedRow?.created_at
            ? new Date(selectedRow.created_at).toLocaleString("es-AR")
            : "",
        }}
        setValues={setEditData}
        saveDisabled={!hasPermission("actualizar cedulas notificacion")}
        sections={[
          {
            title: "Información general",
            description: "Datos solo de lectura.",
            icon: <ContactPageIcon />,
            fields: [
              { name: "code", label: "Código", readOnly: true },
              { name: "stage_text", label: "Etapa", readOnly: true },
              { name: "status_text", label: "Estado", readOnly: true },
              {
                name: "result_status_text",
                label: "Resultado",
                readOnly: true,
              },
              {
                name: "original_name",
                label: "Archivo original",
                readOnly: true,
              },
              {
                name: "storage_path",
                label: "Ruta en storage",
                readOnly: true,
              },
              {
                name: "extracted_legal_file_code",
                label: "Código expediente",
                readOnly: true,
              },
              {
                name: "created_at",
                label: "Creado",
                readOnly: true,
              },
            ],
          },
          {
            title: "Datos editables",
            description: "Campos manuales de la cédula.",
            icon: <EditIcon />,
            fields: [
              {
                name: "label_color",
                label: "Color etiqueta",
                type: "select",
                options: [
                  { value: "yellow", label: "Amarillo" },
                  { value: "orange", label: "Naranja" },
                ],
              },
              {
                name: "label_code",
                label: "Código etiqueta",
              },
              {
                name: "bottom_barcode_code",
                label: "Código barra inferior",
                md: 12,
                readOnly: isSystemGeneratedCertificate(selectedRow),
                helperText: isSystemGeneratedCertificate(selectedRow)
                  ? "Este código fue generado por el sistema y no se puede modificar."
                  : "",
              },
            ],
          },
        ]}
      />

      <AppDialog
        open={openStatusDialog}
        onClose={() => setOpenStatusDialog(false)}
        title={`Registrar estado ${selectedRow ? `- ${selectedRow.code}` : ""}`}
        description="Registre el nuevo estado de la cédula, la fecha del cambio y cualquier nota relevante. Si la cédula se marca como finalizada, podrá agregar información adicional como resultado, color de etiqueta, códigos y un archivo final PDF."
        maxWidth="md"
        icon={<ContactPageIcon />}
        fullScreenMobile
        actions={
          <>
            <Button
              onClick={() => setOpenStatusDialog(false)}
              disabled={statusLoading}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveStatus}
              variant="contained"
              disabled={statusLoading}
            >
              {statusLoading ? "Guardando..." : "Guardar"}
            </Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              select
              fullWidth
              label="Nuevo estado"
              value={statusForm.status}
              onChange={(e) =>
                setStatusForm({ ...statusForm, status: e.target.value })
              }
            >
              <MenuItem value="">SELECCIONAR...</MenuItem>
              {getAllowedStatusOptions().map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Fecha del estado"
              type="datetime-local"
              value={statusForm.status_date}
              onChange={(e) =>
                setStatusForm({ ...statusForm, status_date: e.target.value })
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Notas"
              value={statusForm.notes}
              onChange={(e) =>
                setStatusForm({ ...statusForm, notes: e.target.value })
              }
            />
          </Grid>

          {statusForm.status === "finished" && (
            <>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Resultado"
                  value={statusForm.result_status}
                  onChange={(e) =>
                    setStatusForm({
                      ...statusForm,
                      result_status: e.target.value,
                    })
                  }
                >
                  <MenuItem value="">SELECCIONAR...</MenuItem>
                  <MenuItem value="success">LOGRADO</MenuItem>
                  <MenuItem value="no_success">NO LOGRADO</MenuItem>
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Color etiqueta"
                  value={statusForm.label_color}
                  onChange={(e) =>
                    setStatusForm({
                      ...statusForm,
                      label_color: e.target.value,
                    })
                  }
                >
                  <MenuItem value="">SELECCIONAR...</MenuItem>
                  <MenuItem value="yellow">AMARILLO</MenuItem>
                  <MenuItem value="orange">NARANJA</MenuItem>
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Código etiqueta"
                  value={statusForm.label_code}
                  onChange={(e) =>
                    setStatusForm({ ...statusForm, label_code: e.target.value })
                  }
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Código barra inferior"
                  value={statusForm.bottom_barcode_code}
                  onChange={(e) =>
                    setStatusForm({
                      ...statusForm,
                      bottom_barcode_code: e.target.value,
                    })
                  }
                  InputProps={{
                    readOnly: isSystemGeneratedCertificate(selectedRow),
                  }}
                  helperText={
                    isSystemGeneratedCertificate(selectedRow)
                      ? "Este código fue generado por el sistema y no se puede modificar."
                      : ""
                  }
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Button variant="outlined" component="label" fullWidth>
                  Subir archivo final PDF
                  <input
                    hidden
                    type="file"
                    accept="application/pdf"
                    onChange={(e) =>
                      setStatusForm({
                        ...statusForm,
                        final_file: e.target.files?.[0] || null,
                      })
                    }
                  />
                </Button>

                {statusForm.final_file && (
                  <Typography
                    variant="caption"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Archivo seleccionado: {statusForm.final_file.name}
                  </Typography>
                )}
              </Grid>
            </>
          )}
        </Grid>
      </AppDialog>

      <AppDialog
        open={openPreview}
        onClose={handleClosePreview}
        title="Vista previa del archivo"
        icon={<AttachFileOutlinedIcon />}
        maxWidth="lg"
        fullScreenMobile
        fullScreenBreakpoint="md"
        stickyHeader
        contentSx={{ p: 0 }}
        paperSx={{ borderRadius: isMobile ? 0 : 4, overflow: "hidden" }}
        actions={
          !isMobile ? (
            <Button
              onClick={handleClosePreview}
              sx={{ borderRadius: 2, fontWeight: 800 }}
            >
              Cerrar
            </Button>
          ) : null
        }
      >
        {previewLoading ? (
          <Box sx={{ p: 2 }}>
            <Skeleton variant="rounded" height={isMobile ? 420 : 680} />
          </Box>
        ) : previewUrl ? (
          <iframe
            src={previewUrl}
            style={{
              width: "100%",
              height: isMobile ? "calc(100vh - 56px)" : "82vh",
              border: 0,
            }}
            title="preview"
          />
        ) : (
          <Box sx={{ p: 2 }}>
            <Typography>No se pudo cargar el archivo.</Typography>
          </Box>
        )}
      </AppDialog>

      <AppDialog
        open={openDownloadPdfDialog}
        onClose={handleCloseDownloadPdfDialog}
        title="Descargar PDF de cédulas"
        subtitle="Subí el Excel con códigos de expediente y elegí la etapa para descargar las cédulas generadas."
        icon={<DownloadIcon />}
        maxWidth="sm"
        actions={
          <>
            <Button
              onClick={handleCloseDownloadPdfDialog}
              disabled={downloadPdfLoading}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleDownloadGeneratedPdf}
              disabled={downloadPdfLoading}
            >
              {downloadPdfLoading ? "Descargando..." : "Descargar PDF"}
            </Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <FormControl fullWidth>
              <InputLabel>Etapa</InputLabel>
              <Select
                value={downloadStage}
                onChange={(e) => setDownloadStage(e.target.value)}
                label="Etapa"
              >
                <MenuItem value="">SELECCIONAR...</MenuItem>
                <MenuItem value="stage_1">ETAPA 1</MenuItem>
                <MenuItem value="stage_2">ETAPA 2</MenuItem>
                <MenuItem value="stage_3">ETAPA 3</MenuItem>
                <MenuItem value="stage_4">ETAPA 4</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Button variant="outlined" component="label" fullWidth>
              Seleccionar Excel
              <input
                hidden
                type="file"
                accept=".xls,.xlsx"
                ref={downloadExcelRef}
                onChange={handleDownloadExcelChange}
              />
            </Button>
          </Grid>

          {downloadExcelFile && (
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Archivo seleccionado"
                value={downloadExcelFile.name}
                fullWidth
                InputProps={{ readOnly: true }}
              />
            </Grid>
          )}
        </Grid>
      </AppDialog>

      <NotificationCertificateTrackDialog
        open={openTrackDialog}
        onClose={handleCloseTrackDialog}
        row={selectedTrackRow}
      />

      <ConfirmDialog
        open={openDeleteConfirm}
        onClose={() => {
          setOpenDeleteConfirm(false);
          setSelectedDeleteId(null);
        }}
        onConfirm={handleDelete}
        title="Confirmar eliminación"
        description="¿Quieres eliminar esta cédula de notificación? Esta acción no se puede deshacer."
        confirmText={deleteLoading ? "Eliminando..." : "Eliminar"}
        cancelText="Cancelar"
        confirmColor="error"
        loading={deleteLoading}
      />
    </Grid>
  );
}
